import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Sun, Moon } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useInstagramAccounts } from '../../context/InstagramAccountContext';
import { SUPPORTED_LOCALES, type LocaleCode } from '../../config/i18n';
import { panelPath } from '../../config/paths';
import NotificationBell from './NotificationBell';

const SWITCH_OVERLAY_MS = 280;

export default function PanelHeader() {
  const { user } = useAuth();
  const { resolved, setMode } = useTheme();
  const { activeAccount } = useInstagramAccounts();
  const { t, i18n } = useTranslation();
  const [switching, setSwitching] = useState(false);

  const displayName = user?.name?.trim() || user?.email || '';
  const initials = displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join('') || '?';
  const credits = user?.aiCredits.balance ?? 0;
  const handle = activeAccount?.igUsername
    ? `@${activeAccount.igUsername}`
    : activeAccount?.pageName || user?.email || '';
  const isDark = resolved === 'dark';
  const currentLang = SUPPORTED_LOCALES.includes(i18n.language as LocaleCode) ? i18n.language : 'tr';

  const changeLanguage = async (locale: string) => {
    if (locale === currentLang || switching) return;
    setSwitching(true);
    try {
      await i18n.changeLanguage(locale);
      await new Promise((r) => setTimeout(r, SWITCH_OVERLAY_MS));
    } finally {
      setSwitching(false);
    }
  };

  const toggleTheme = () => setMode(isDark ? 'light' : 'dark');

  return (
    <>
      <header className="sticky top-0 z-20 border-b border-[#e8ddd0] dark:border-lc-border bg-[#f5eff5] dark:bg-lc-surface px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
        <Link
          to={panelPath('/billing')}
          className="flex items-center gap-2.5 shrink-0"
          title={t('theme.credits')}
        >
          <span className="text-[11px] font-semibold uppercase tracking-wide text-[#7a6b5d] dark:text-lc-faint">
            {t('theme.creditsShort')}
          </span>
          <span className="text-lg font-bold tabular-nums text-lc-pink">{credits.toLocaleString()}</span>
        </Link>

        <div className="flex items-center gap-3 sm:gap-4">
          <div className="flex items-center gap-1 rounded-md border border-[#e8ddd0] dark:border-lc-border bg-white dark:bg-lc-bg px-1.5 py-1">
            {SUPPORTED_LOCALES.map((locale, i) => {
              const active = currentLang === locale;
              return (
                <React.Fragment key={locale}>
                  {i > 0 && <div className="w-px h-4 bg-[#e8ddd0] dark:bg-lc-border" />}
                  <button
                    type="button"
                    onClick={() => void changeLanguage(locale)}
                    disabled={switching}
                    aria-pressed={active}
                    className={`px-2 py-1 text-[11px] font-semibold uppercase transition-colors disabled:opacity-60 ${
                      active
                        ? 'text-lc-pink'
                        : 'text-[#7a6b5d] dark:text-lc-faint hover:text-[#241c14] dark:hover:text-lc-text'
                    }`}
                  >
                    {locale}
                  </button>
                </React.Fragment>
              );
            })}
          </div>

          <NotificationBell />

          <button
            type="button"
            onClick={toggleTheme}
            className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-[#e8ddd0] dark:border-lc-border bg-transparent text-[#241c14] dark:text-lc-text text-xs font-medium hover:bg-white/60 dark:hover:bg-lc-bg/60 transition-colors"
            title={t('theme.toggle', { mode: t(isDark ? 'theme.dark' : 'theme.light') })}
          >
            {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            <span className="hidden sm:inline">{isDark ? 'Light' : 'Dark'}</span>
          </button>

          <div className="flex items-center gap-3 pl-3 sm:pl-4 border-l border-[#e8ddd0] dark:border-lc-border">
            <div className="hidden sm:flex flex-col items-end min-w-0">
              <span className="text-xs font-semibold text-[#241c14] dark:text-lc-text truncate max-w-[10rem]">
                {displayName}
              </span>
              {handle && (
                <span className="text-[10px] font-medium text-[#7a6b5d] dark:text-lc-faint truncate max-w-[10rem]">
                  {handle}
                </span>
              )}
            </div>
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-lc-pink text-sm font-bold text-white dark:text-lc-ink">
              {initials}
            </div>
          </div>
        </div>
      </header>

      {switching &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#faf6f0]/90 dark:bg-lc-bg/90"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <div className="flex flex-col items-center gap-2 text-[#7a6b5d] dark:text-lc-muted">
              <Loader2 className="h-7 w-7 animate-spin text-lc-pink" aria-hidden />
              <span className="text-sm font-medium">{t('common.loading')}</span>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
